import z from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { userIdSchema } from './ids';

export const USER_CREATE_TOKEN_TYPE = 'user/create-token';
export const USER_ATTACH_SIGN_IN_CLAIMS_TYPE = 'user/attach-sign-in-claims';

export const tokenScope = {
    WEBSOCKET: 'websocket',
    SIGN_IN: 'sign-in',
    EMAIL_VERIFICATION: 'email-verification',
    IDENTITY_CONFIRMATION: 'identity-confirmation',
} as const;

export const userCreateToken = defineCommand(
    USER_CREATE_TOKEN_TYPE,
    z.object({
        practiceId: z.string(),
        practiceMemberId: z.string(),
        sessionId: z.string(),
        // Only the scopes this command may mint — email-verification tokens are issued by the
        // sign-up / resend paths, never on request.
        scope: z.enum([tokenScope.WEBSOCKET, tokenScope.SIGN_IN]),
    }),
    z.object({ token: z.string() }),
);

export const userAttachSignInClaims = defineCommand(
    USER_ATTACH_SIGN_IN_CLAIMS_TYPE,
    z.object({
        userId: userIdSchema.meta(metadata.nonSensitive),
        claims: z.record(z.string(), z.unknown()).meta(metadata.nonSensitive),
    }),
);

export const USER_SIGN_IN_BY_TOKEN_TYPE = 'user/sign-in-by-token';

// Completes a sign-in that had to wait for another context to finish provisioning the user;
// `ready: false` means the claims have not arrived yet and the caller should ask again.
export const userSignInByToken = defineCommand(
    USER_SIGN_IN_BY_TOKEN_TYPE,
    z.object({ signInTokenId: z.string() }),
    z.discriminatedUnion('ready', [
        z.object({ ready: z.literal(false) }),
        z.object({
            ready: z.literal(true),
            sessionId: z.string(),
            maxAge: z.number(),
        }),
    ]),
);

export const USER_SIGN_IN_WITH_PASSWORD_TYPE = 'user/sign-in-with-password';

export const userSignInWithPassword = defineCommand(
    USER_SIGN_IN_WITH_PASSWORD_TYPE,
    z.object({
        email: z.email().min(1),
        password: z.string().min(1),
        returnUrl: z.url().optional(),
        role: z.string().meta(metadata.nonSensitive),
        encodedData: z.string().optional(),
    }),
    z.object({
        sessionId: z.string(),
        maxAge: z.number(),
        returnUrl: z.url().optional(),
    }),
);
